import { CENTROS, OPTOS, OPC, SUGERIDOS, edadTexto, esRevision, soloRevision } from "@/lib/api";
import { TextField, SelectField, DateField, Section } from "@/components/Fields";
import { RemitentesList } from "@/components/RemitentesList";
import { ClavesCuestionario } from "@/components/CuestionariosPaciente";
import { Textarea } from "@/components/ui/textarea";
import { RespuestaDesglose } from "@/components/RespuestaDesglose";
import { toast } from "sonner";

const DATOS = [["dni", "DNI (para factura)"], ["profesion", "Profesión / estudios del paciente"], ["direccion", "Dirección", 2], ["codigo_postal", "Código postal"], ["poblacion", "Población"],
  ["telefono2", "Segundo móvil"], ["telefono_fijo", "Teléfono fijo / casa"], ["telefono_trabajo", "Teléfono trabajo"], ["nombre_padre", "Nombre del padre"],
  ["profesion_padre", "Profesión del padre"], ["nombre_madre", "Nombre de la madre"], ["profesion_madre", "Profesión de la madre"], ["profesion_tutor", "Profesión del tutor/a"],
  ["desde_cuando", "¿Desde cuándo le ocurre?"], ["conoce_oc", "¿Conoce la Optometría Comportamental?"]];

const DatosPersonales = ({ f, set }) => (
  <details className="col-span-full rounded-md border border-slate-200 px-3 py-2" data-testid="pf-datos-personales" open={DATOS.some(([k]) => f[k])}>
    <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-slate-600">Más datos personales (cuestionario)</summary>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
      {DATOS.map(([k, label, span]) => (
        <div key={k} className={span ? "col-span-2" : ""}><TextField label={label} value={f[k]} onChange={set(k)} testId={`pf-${k.replace("_", "-")}`} /></div>
      ))}
    </div>
  </details>
);

const Sugeridos = ({ value, onChange, skip = [], prefix = "sug", onCls = "bg-[#0D9488] border-[#0D9488] text-white" }) => (
  <div className="flex flex-wrap gap-2">
    {SUGERIDOS.filter((s) => !skip.includes(s.id)).map((s) => {
      const on = value.includes(s.id);
      return (
        <button key={s.id} type="button" data-testid={`${prefix}-${s.id}`} onClick={() => onChange(on ? value.filter((x) => x !== s.id) : [...value, s.id])}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? onCls : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>
          {s.label}
        </button>
      );
    })}
  </div>
);

const Valorar = ({ f, set, onPasar }) => (
  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/50 p-3" data-testid="pf-valorar">
    <div className="flex flex-wrap items-center gap-2 mb-2">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">A valorar después (sin presupuesto todavía; avisa al terminar Syntonic)</span>
      {(f.tratamientos_valorar || []).length > 0 && <button type="button" onClick={onPasar} data-testid="valorar-pasar-btn" className="ml-auto rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700">Pasar a sugeridos para el presupuesto</button>}
    </div>
    <Sugeridos value={f.tratamientos_valorar || []} onChange={set("tratamientos_valorar")} skip={["revision", "syntonic"]} prefix="valorar" onCls="bg-amber-500 border-amber-500 text-white" />
    <label className="block mt-3">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Observaciones del tratamiento</span>
      <Textarea data-testid="pf-obs-tratamiento" value={f.obs_tratamiento || ""} onChange={(e) => set("obs_tratamiento")(e.target.value)} rows={2} className="text-sm bg-white" placeholder="Ej.: primero Syntonic; después valorar terapia visual y gafas" />
    </label>
  </div>
);

const pasarValorar = (f, set, rev) => () => {
  const v = f.tratamientos_valorar || [];
  set("tratamientos_sugeridos")([...new Set([...(rev && (f.nuevo_tto || "").toUpperCase() !== "SI" ? [] : f.tratamientos_sugeridos || []), ...v])]);
  if (rev) set("nuevo_tto")("SI");
  set("tratamientos_valorar")([]);
  toast.success("Pasados a sugeridos. Ya puedes hacer el presupuesto.");
};

const edadDetalle = (iso) => {
  if (!iso) return "";
  const n = new Date(`${iso.slice(0, 10)}T00:00:00`);
  const h = new Date();
  let m = (h.getFullYear() - n.getFullYear()) * 12 + h.getMonth() - n.getMonth() - (h.getDate() < n.getDate() ? 1 : 0);
  if (Number.isNaN(m) || m < 0) return "";
  const a = Math.floor(m / 12);
  m %= 12;
  return `${a} año${a === 1 ? "" : "s"}${m ? ` y ${m} mes${m === 1 ? "" : "es"}` : ""}`;
};

const EdadBloque = ({ f }) => (
  <div>
    <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Edad</span>
    <div data-testid="pf-edad-calculada" className="flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-800">
      {edadDetalle(f.fecha_nacimiento) || edadTexto(f) || <span className="text-slate-400 font-normal">—</span>}
    </div>
  </div>
);

const MESES = { "1 MES": 1, "3 MESES": 3, "6 MESES": 6, "1 AÑO": 12 };
const ProxRevision = ({ f, set }) => {
  const onEn = (v) => {
    set("prox_revision_en")(v);
    if (!MESES[v]) return;
    const d = new Date(`${(f.fecha_ee || new Date().toISOString()).slice(0, 10)}T00:00:00`);
    d.setMonth(d.getMonth() + MESES[v]);
    set("fecha_prox_revision")(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
    set("estado_prox_revision")("");
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 rounded-md border border-sky-200 bg-sky-50/50 p-3" data-testid="pf-prox-revision">
      <SelectField label="Próxima revisión en" value={f.prox_revision_en} onChange={onEn} options={OPC.prox_revision_en} testId="pf-prox-rev-en" plain />
      <DateField label="Fecha próxima revisión" value={f.fecha_prox_revision} onChange={set("fecha_prox_revision")} testId="pf-prox-rev-fecha" />
      <p className="col-span-2 self-end text-[11px] text-slate-500">Saldrá en la pestaña «Revisiones programadas» y en tareas cuando llegue el mes.</p>
    </div>
  );
};

const RevisionFields = ({ f, set }) => {
  const onTto = (v) => {
    set("tratamientos_sugeridos")(v);
    set("nuevo_tto")(v.length ? "SI" : "NO");
  };
  const nuevo = (f.nuevo_tto || "").toUpperCase() === "SI";
  return (
    <>
      <SelectField label="Revisión" value={f.momento_rev} onChange={set("momento_rev")} options={OPC.momento_rev} testId="pf-momento-rev" />
      <div className="col-span-2 md:col-span-4" data-testid="pf-rev-tratamientos">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">¿Necesita tratamiento? (marca todos los que necesite; ninguno = sigue igual)</span>
        <Sugeridos value={nuevo ? f.tratamientos_sugeridos || [] : []} onChange={onTto} skip={["revision"]} />
        <Valorar f={f} set={set} onPasar={pasarValorar(f, set, true)} />
      </div>
      <SelectField label="Presupuesto" value={nuevo ? f.envio_ppto : f.ppto_rev} onChange={set(nuevo ? "envio_ppto" : "ppto_rev")} options={OPC.envio_ppto} testId="pf-ppto-rev" />
      <SelectField label="Respuesta" value={nuevo ? f.respuesta : f.respuesta_rev} onChange={set(nuevo ? "respuesta" : "respuesta_rev")} options={OPC.respuesta} testId="pf-respuesta-rev" />
      <p className="col-span-2 md:col-span-4 text-xs text-slate-500" data-testid="pf-rev-hint">{nuevo
        ? "Necesita tratamiento: este presupuesto y respuesta son los del tratamiento. Al marcar APROBADO y guardar se crean los tratamientos marcados."
        : "Revisión dentro del tratamiento: el presupuesto se envía antes del informe y el tratamiento sigue igual."}</p>
      <div className="col-span-2 md:col-span-4"><ProxRevision f={f} set={set} /></div>
    </>
  );
};

const PvFields = ({ f, set }) => (
  <>
    <SelectField label="Necesita PV" value={f.necesita_pv} onChange={set("necesita_pv")} options={OPC.necesita_pv} testId="pf-necesita-pv" plain />
    {["SI", "LATERALIDAD"].includes((f.necesita_pv || "").toUpperCase()) ? (
      <>
        <SelectField label="Presupuesto PV" value={f.ppto_pv} onChange={set("ppto_pv")} options={OPC.envio_ppto} testId="pf-ppto-pv" />
        <SelectField label="Respuesta PV" value={f.respuesta_pv} onChange={set("respuesta_pv")} options={OPC.respuesta} testId="pf-respuesta-pv" />
        <DateField label="Cita PV" value={f.fecha_pv} onChange={set("fecha_pv")} testId="pf-fecha-pv" />
      </>
    ) : (
      <DateField label="Fecha PV" value={f.fecha_pv} onChange={set("fecha_pv")} testId="pf-fecha-pv" />
    )}
  </>
);

export const ProcessForm = ({ f, set }) => {
  const isRev = esRevision(f);
  const rev = soloRevision(f);
  return (
  <div className="space-y-4">
    <Section title="1 · Datos del paciente" testId="sec-datos">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="col-span-2"><TextField label="Nombre" value={f.nombre} onChange={set("nombre")} testId="pf-nombre" /></div>
        <TextField label="Código" value={f.codigo} onChange={set("codigo")} testId="pf-codigo" />
        <DateField label="Fecha de nacimiento" value={f.fecha_nacimiento} onChange={set("fecha_nacimiento")} testId="pf-fecha-nacimiento" />
        <EdadBloque f={f} />
        <SelectField label="Centro" value={f.centro} onChange={set("centro")} options={CENTROS} testId="pf-centro" />
        <TextField label="Teléfono" value={f.telefono} onChange={set("telefono")} testId="pf-telefono" />
        <TextField label="Email" value={f.email} onChange={set("email")} testId="pf-email" />
        <TextField label="Padre/madre/tutor" value={f.tutor} onChange={set("tutor")} testId="pf-tutor" />
        <TextField label="Colegio" value={f.colegio} onChange={set("colegio")} testId="pf-colegio" />
        <TextField label="Curso" value={f.curso} onChange={set("curso")} testId="pf-curso" />
        <div className="col-span-2"><TextField label="Motivo de consulta" value={f.motivo_consulta} onChange={set("motivo_consulta")} testId="pf-motivo-consulta" /></div>
        <DatosPersonales f={f} set={set} />
        <TextField label="Quién remite" value={f.quien_remite} onChange={set("quien_remite")} list="pf-remite" testId="pf-remite" />
        <SelectField label="Optometrista" value={f.optometrista} onChange={set("optometrista")} options={OPTOS} testId="pf-opto" />
        <SelectField label="Terapeuta" value={f.terapeuta} onChange={set("terapeuta")} options={OPTOS} testId="pf-terapeuta" />
        <DateField label="Fecha llamada" value={f.fecha_llamada} onChange={set("fecha_llamada")} testId="pf-fecha-llamada" />
        <SelectField label="Cita" value={f.estado_cita} onChange={set("estado_cita")} options={OPC.estado_cita} testId="pf-estado-cita" />
        <DateField label="Fecha cita" value={f.fecha_cita} onChange={set("fecha_cita")} testId="pf-fecha-cita" />
        {f.estado_cita === "ANULADA" && (
          <>
            <SelectField label="Motivo anulación" value={f.motivo_anulacion} onChange={set("motivo_anulacion")} options={OPC.motivo_anulacion} testId="pf-motivo-anulacion" plain />
            <TextField label="Detalle" value={f.motivo_detalle} onChange={set("motivo_detalle")} testId="pf-motivo-detalle" />
            <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="pf-volver-llamar" checked={!!f.volver_a_llamar} onChange={(e) => set("volver_a_llamar")(e.target.checked)} className="h-4 w-4 accent-orange-600" />
              Volver a llamar
            </label>
            {f.volver_a_llamar && <DateField label="Llamar el" value={f.fecha_rellamada} onChange={set("fecha_rellamada")} testId="pf-fecha-rellamada" />}
          </>
        )}
        <RemitentesList id="pf-remite" />
      </div>
    </Section>
    <Section title={isRev ? "2 · Revisión y presupuesto de revisión" : "2 · Evaluación (EE / PV)"} testId="sec-evaluacion">
      {f.id && <ClavesCuestionario patientId={f.id} motivo={f.motivo_consulta} />}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <DateField label={isRev ? "Fecha revisión" : "Fecha EE"} value={f.fecha_ee} onChange={set("fecha_ee")} testId="pf-fecha-ee" />
        <SelectField label="Tipo de examen" value={f.tipo_examen} onChange={set("tipo_examen")} options={OPC.tipo_examen} testId="pf-tipo-examen" />
        <SelectField label="Evaluación auditiva" value={f.eval_auditiva} onChange={set("eval_auditiva")} options={OPC.eval_auditiva} testId="pf-eval-auditiva" />
        {isRev ? <RevisionFields f={f} set={set} /> : <PvFields f={f} set={set} />}
      </div>
    </Section>
    <Section title={isRev ? "3 · Informe" : "3 · Informe y presupuesto"} testId="sec-informe">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <SelectField label="Tipo de informe" value={f.tipo_informe} onChange={set("tipo_informe")} options={OPC.tipo_informe} testId="pf-tipo-informe" />
        <SelectField label="Estado informe" value={f.estado_informe} onChange={set("estado_informe")} options={OPC.estado_informe} testId="pf-estado-informe" />
        {!isRev && <SelectField label="Envío presupuesto" value={f.envio_ppto} onChange={set("envio_ppto")} options={OPC.envio_ppto} testId="pf-envio-ppto" />}
      </div>
      {!isRev && <>
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Tratamientos sugeridos</span>
        <Sugeridos value={f.tratamientos_sugeridos} onChange={set("tratamientos_sugeridos")} />
        {(f.tratamientos_sugeridos || []).includes("revision") && <ProxRevision f={f} set={set} />}
        <Valorar f={f} set={set} onPasar={pasarValorar(f, set, false)} />
      </>}
    </Section>
    <Section title={rev ? "4 · Observaciones" : "4 · Respuesta y terapia"} testId="sec-respuesta">
      {!rev && <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {!isRev && <RespuestaDesglose f={f} set={set} />}
        <SelectField label="Respuesta oído" value={f.respuesta_oido} onChange={set("respuesta_oido")} options={OPC.respuesta_oido} testId="pf-respuesta-oido" />
        <SelectField label="Hora terapia" value={f.hora_terapia} onChange={set("hora_terapia")} options={OPC.hora_terapia} testId="pf-hora-terapia" />
        <TextField label="Día / hora" value={f.dia_hora} onChange={set("dia_hora")} testId="pf-dia-hora" placeholder="Miércoles 17:15" />
      </div>}
      {!rev && <p className="text-xs text-slate-500 mt-3">Pulsa <b>Resp. tratamiento visual</b> para apuntar qué aprueban de cada tratamiento. Al guardar se crean automáticamente solo los aprobados.</p>}
      {rev && (f.momento_rev || "").toUpperCase() === "FINAL" && <p className="text-xs text-purple-700 mt-3" data-testid="pf-rev-final-hint">Revisión final: cuando el informe esté enviado, decide si dar de alta con el botón «Dar de alta».</p>}
      <label className="block mt-4">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Observaciones</span>
        <Textarea data-testid="pf-observaciones" value={f.observaciones} onChange={(e) => set("observaciones")(e.target.value)} rows={3} className="text-sm bg-white" />
      </label>
    </Section>
  </div>
  );
};
